import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';

// --- The Advanced Editor Component ---
function ProductEditor({ product, onSave, onCancel }) {
    const [productName, setProductName] = useState(product.productName || '');
    const [attributes, setAttributes] = useState(product.attributes || []);
    const [options, setOptions] = useState(product.options || {});
    // MODIFIED: Added a unique ID to each rule to serve as a stable key
    const [rules, setRules] = useState(product.rules || []);
    const [customers, setCustomers] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                const snapshot = await getDocs(collection(db, 'customers'));
                setCustomers(snapshot.docs.map(doc => doc.data()));
            } catch (error) {
                console.error("Failed to fetch customers for rule editor:", error);
            }
        };
        fetchCustomers();
    }, []);

    const addAttribute = () => {
        const newAttribute = prompt("Enter new attribute name (e.g., Grade, Formulation):");
        if (newAttribute && !attributes.includes(newAttribute)) {
            setAttributes([...attributes, newAttribute]);
            // This part is correct: initialize the options array for the new attribute
            setOptions(prev => ({ ...prev, [newAttribute]: [] }));
        }
    };

    // --- THIS FUNCTION IS CORRECTED ---
    const addOptionToAttribute = (attributeName) => {
        const newOption = prompt(`Enter new option for ${attributeName}:`);
        if (!newOption) return; // Exit if the user cancels the prompt

        setOptions(prevOptions => {
            const currentOptions = prevOptions[attributeName] || [];
            // Check for duplicates before adding
            if (currentOptions.includes(newOption)) {
                alert(`Option "${newOption}" already exists for ${attributeName}.`);
                return prevOptions;
            }
            // Create a new object for the state update
            return {
                ...prevOptions,
                [attributeName]: [...currentOptions, newOption]
            };
        });
    };

    // MODIFIED: Add a unique ID to each new rule
    const addRule = () => {
        const newRule = { 
            id: Date.now(), // Unique key for React's rendering
            conditions: {}, 
            result: { batchCode: '', coaPrefix: '' } 
        };
        setRules([...rules, newRule]);
    };

    const removeRule = (ruleId) => {
        setRules(rules.filter(r => r.id !== ruleId));
    };

    const handleRuleChange = (ruleId, field, value, subField = null) => {
        const newRules = rules.map(rule => {
            if (rule.id === ruleId) {
                const updatedRule = { ...rule };
                if (field === 'result') {
                    updatedRule.result = { ...updatedRule.result, [subField]: value };
                } else {
                    updatedRule.conditions = { ...updatedRule.conditions, [field]: value };
                }
                return updatedRule;
            }
            return rule;
        });
        setRules(newRules);
    };

    const handleSave = async () => {
        if (!productName.trim()) { alert("Product Name is required."); return; }
        setIsSaving(true);
        // Clean the 'id' field from rules before saving to Firestore
        const rulesToSave = rules.map(({ id, ...rest }) => rest);
        const productData = { productName, attributes, options, rules: rulesToSave };
        
        try {
            if (product.id) {
                await updateDoc(doc(db, 'products', product.id), productData);
            } else {
                await addDoc(collection(db, 'products'), productData);
            }
            onSave();
        } catch (error) {
            console.error("Error saving product:", error);
            alert("Failed to save product logic.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div>
            <h2>{product.id ? `Edit Logic for ${product.productName}` : 'Create New Product'}</h2>
            <fieldset style={{marginBottom: '15px'}}><legend>1. Product Name</legend><input type="text" value={productName} onChange={e => setProductName(e.target.value)} placeholder="Product Name" /></fieldset>
            
            <fieldset style={{marginBottom: '15px'}}><legend>2. Form Attributes & Options</legend><button type="button" onClick={addAttribute}>+ Add Attribute</button>{attributes.map(attr => (<div key={attr} style={{border: '1px solid #eee', padding: '10px', margin: '10px 0', borderRadius: '5px'}}><p><strong>{attr}</strong></p><div>{options[attr]?.map(opt => <span key={opt} style={{marginRight: '10px', background: '#f0f0f0', padding: '2px 5px', borderRadius: '4px'}}>{opt}</span>)}</div><button type="button" onClick={() => addOptionToAttribute(attr)} style={{marginTop: '5px'}}>+ Add Option</button></div>))}</fieldset>
            
            <fieldset><legend>3. Batch Code & COA Prefix Rules</legend><button type="button" onClick={addRule}>+ Add Rule</button>
                {/* MODIFIED: Using the new stable `rule.id` as the key */}
                {rules.map(rule => (
                    <div key={rule.id} style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '15px', border: '1px solid #ddd', padding: '10px', margin: '10px 0', borderRadius: '5px'}}>
                        <div><strong>IF</strong>
                            <div>Company =
                                <select value={rule.conditions['Company'] || ''} onChange={e => handleRuleChange(rule.id, 'Company', e.target.value)}>
                                    <option value="">Any</option>
                                    {customers.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                                </select>
                            </div>
                            {/* This section will now correctly re-render when `options` state changes */}
                            {attributes.map(attr => (
                                <div key={attr}>{attr} =
                                    <select value={rule.conditions[attr] || ''} onChange={e => handleRuleChange(rule.id, attr, e.target.value)}>
                                        <option value="">Any</option>
                                        {/* This mapping is now reliable */}
                                        {options[attr]?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                    </select>
                                </div>
                            ))}
                        </div>
                        <div><strong>THEN</strong>
                            <div><label>Result Batch Code =</label>
                                <input type="text" value={rule.result?.batchCode || ''} onChange={e => handleRuleChange(rule.id, 'result', e.target.value, 'batchCode')} placeholder="e.g., H98" required/>
                            </div>
                            <div style={{marginTop: '5px'}}><label>Result COA Prefix =</label>
                                <input type="text" value={rule.result?.coaPrefix || ''} onChange={e => handleRuleChange(rule.id, 'result', e.target.value, 'coaPrefix')} placeholder="e.g., CHQ (optional)"/>
                            </div>
                        </div>
                        <button type="button" onClick={() => removeRule(rule.id)}>Remove Rule</button>
                    </div>
                ))}
            </fieldset>
            
            <div style={{marginTop: '20px'}}><button onClick={handleSave} disabled={isSaving}>{isSaving ? 'Saving...' : 'Save Full Product Logic'}</button><button onClick={onCancel} disabled={isSaving} style={{marginLeft: '10px'}}>Cancel</button></div>
        </div>
    );
}


// --- The Main Management Component (View Switcher) ---
function ProductManagement() {
    // ... This outer component remains the same ...
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingProduct, setEditingProduct] = useState(null);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const querySnapshot = await getDocs(collection(db, 'products'));
            const fetchedProducts = querySnapshot.docs.map(doc => {
                const data = doc.data();
                // Ensure each rule has a unique ID for the editor state
                const rulesWithIds = (data.rules || []).map(rule => ({ ...rule, id: Math.random() }));
                return { id: doc.id, ...data, rules: rulesWithIds };
            });
            setProducts(fetchedProducts);
        } catch (error) { console.error("Error fetching products:", error); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchProducts(); }, []);

    const handleDelete = async (productId) => {
        if (!window.confirm("Are you sure you want to delete this product?")) return;
        try {
            await deleteDoc(doc(db, 'products', productId));
            fetchProducts();
        } catch (error) {
            console.error("Error deleting product:", error);
            alert("Failed to delete product.");
        }
    };
    
    if (loading) return <p>Loading product data...</p>;

    if (editingProduct) {
        return (
            <ProductEditor 
                product={editingProduct} 
                onSave={() => { setEditingProduct(null); fetchProducts(); }}
                onCancel={() => setEditingProduct(null)}
            />
        );
    }

    return (
        <div>
            <h2>Product Logic Management</h2>
            <p>Define products and the business logic for their generation.</p>
            <button onClick={() => setEditingProduct({})} style={{ marginBottom: '20px' }}>
                + Add New Product
            </button>
            <div style={{ display: 'grid', gap: '15px' }}>
                {products.length > 0 ? products.map(p => (
                    <div key={p.id} style={{ border: '1px solid #ccc', padding: '15px' }}>
                        <h3>{p.productName}</h3>
                        <p><strong>{p.rules?.length || 0}</strong> rules defined.</p>
                        <p><strong>{p.attributes?.length || 0}</strong> attributes defined.</p>
                        <button onClick={() => setEditingProduct(p)}>Edit Full Logic</button>
                        <button onClick={() => handleDelete(p.id)} style={{ marginLeft: '10px' }}>Delete</button>
                    </div>
                )) : (
                    <p>No products defined yet. Click "Add New Product" to start.</p>
                )}
            </div>
        </div>
    );
}

export default ProductManagement;